import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Lightbulb, ChevronDown, ChevronUp, Sparkles, Terminal, Copy, Check } from 'lucide-react';
import type { StepHint, HintLevel } from '@/types/lesson';

// Ключ для сохранения состояния в localStorage
const HINT_EXPANDED_KEY = 'git-course-hint-expanded';

interface CommandHintProps {
  hint: StepHint;
  currentLevel: HintLevel;
  onShowNextHint: () => void;
  onUseSolution: () => void;
  maxLevel?: HintLevel;
  /** Компактный режим - свёрнут по умолчанию */
  compact?: boolean;
}

/**
 * Компонент для отображения подсказок по командам
 * Оптимизированная версия с компактным режимом
 *
 * Уровни подсказок:
 * - Уровень 1: Общее описание задачи
 * - Уровень 2: Название команды
 * - Уровень 3: Полная команда с аргументами
 */
export function CommandHint({
  hint,
  currentLevel,
  onShowNextHint,
  onUseSolution,
  maxLevel = 3,
  compact = true,
}: CommandHintProps) {
  // Загружаем сохранённое состояние или используем compact как default
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem(HINT_EXPANDED_KEY);
      return saved !== null ? saved === 'true' : !compact;
    }
    return !compact;
  });

  const [copied, setCopied] = useState(false);

  // Сохраняем состояние при изменении
  useEffect(() => {
    localStorage.setItem(HINT_EXPANDED_KEY, String(isExpanded));
  }, [isExpanded]);

  const getHintContent = (level: HintLevel): string => {
    switch (level) {
      case 1:
        return hint.level1;
      case 2:
        return hint.level2;
      case 3:
        return hint.level3;
    }
  };

  const getLevelIcon = (level: HintLevel) => {
    switch (level) {
      case 1:
        return <Lightbulb className="h-3 w-3" />;
      case 2:
        return <Terminal className="h-3 w-3" />;
      case 3:
        return <Sparkles className="h-3 w-3" />;
    }
  };

  const getLevelColor = (level: HintLevel): string => {
    switch (level) {
      case 1:
        return 'text-yellow-400 border-yellow-400/20 bg-yellow-400/5';
      case 2:
        return 'text-blue-400 border-blue-400/20 bg-blue-400/5';
      case 3:
        return 'text-green-400 border-green-400/20 bg-green-400/5';
    }
  };

  const canShowNextHint = currentLevel < maxLevel;
  const currentContent = getHintContent(currentLevel);

  // Копирование команды в буфер
  const handleCopy = async () => {
    // На уровне 3 подсказка обычно содержит команду
    const command = currentContent.startsWith('git ') ? currentContent : hint.level3;
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="rounded-md border border-slate-700/50 bg-slate-900/30 overflow-hidden text-xs">
      {/* Header - компактный */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-2.5 py-1.5 text-left hover:bg-slate-800/30 transition-colors"
      >
        <div className="flex items-center gap-1.5">
          {getLevelIcon(currentLevel)}
          <span className="text-slate-400">
            {isExpanded
              ? 'Подсказка'
              : currentContent.slice(0, 50) + (currentContent.length > 50 ? '...' : '')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Мини-индикатор уровня */}
          <div className="flex items-center gap-0.5">
            {[1, 2, 3].map((level) => (
              <div
                key={level}
                className={cn(
                  'w-1.5 h-1.5 rounded-full transition-colors',
                  level <= currentLevel
                    ? level === 1
                      ? 'bg-yellow-400'
                      : level === 2
                        ? 'bg-blue-400'
                        : 'bg-green-400'
                    : 'bg-slate-600'
                )}
              />
            ))}
          </div>
          {isExpanded ? (
            <ChevronUp className="h-3 w-3 text-slate-500" />
          ) : (
            <ChevronDown className="h-3 w-3 text-slate-500" />
          )}
        </div>
      </button>

      {/* Content - развёрнутый */}
      {isExpanded && (
        <div className="px-2.5 pb-2 space-y-2">
          {/* Текущая подсказка */}
          <div className={cn('rounded border px-2 py-1.5', getLevelColor(currentLevel))}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1">
                <span className="font-mono text-xs">{currentContent}</span>
              </div>
              {/* Кнопка копирования для уровня 3 */}
              {currentLevel === 3 && (
                <button
                  onClick={handleCopy}
                  className="p-1 hover:bg-slate-700/50 rounded transition-colors"
                  title="Копировать команду"
                >
                  {copied ? (
                    <Check className="h-3 w-3 text-green-400" />
                  ) : (
                    <Copy className="h-3 w-3 text-slate-400" />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Кнопки действий */}
          <div className="flex items-center gap-1.5">
            {canShowNextHint && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onShowNextHint}
                className="h-6 px-2 text-xs text-slate-400 hover:text-slate-200"
              >
                Ещё
              </Button>
            )}
            {currentLevel === maxLevel && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onUseSolution}
                className="h-6 px-2 text-xs text-green-400 hover:text-green-300 hover:bg-green-400/10"
              >
                Выполнить
              </Button>
            )}
            <span className="ml-auto text-[10px] text-slate-500">
              {currentLevel}/{maxLevel}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Компактный inline-вариант подсказки
 * Для использования внутри других компонентов
 */
interface InlineHintProps {
  hint: StepHint;
  currentLevel: HintLevel;
  onShowNextHint: () => void;
  onUseSolution: () => void;
  maxLevel?: HintLevel;
}

export function InlineHint({
  hint,
  currentLevel,
  onShowNextHint,
  onUseSolution,
  maxLevel = 3,
}: InlineHintProps) {
  const getHintContent = (level: HintLevel): string => {
    switch (level) {
      case 1:
        return hint.level1;
      case 2:
        return hint.level2;
      case 3:
        return hint.level3;
    }
  };

  const canShowNextHint = currentLevel < maxLevel;

  return (
    <div className="inline-flex items-center gap-1.5 rounded bg-slate-800/50 px-2 py-1 text-xs">
      <Lightbulb className="h-3 w-3 text-yellow-400" />
      <span className="text-slate-300 font-mono">{getHintContent(currentLevel)}</span>
      {canShowNextHint && (
        <button
          onClick={onShowNextHint}
          className="text-slate-500 hover:text-slate-300 transition-colors"
        >
          →
        </button>
      )}
      {currentLevel === maxLevel && (
        <button
          onClick={onUseSolution}
          className="text-green-400 hover:text-green-300 transition-colors"
          title="Использовать решение"
        >
          ✓
        </button>
      )}
    </div>
  );
}

/**
 * Компонент для отображения ожидаемой команды
 */
interface ExpectedCommandProps {
  expectedCommand: string;
  validationRegex?: string;
  showExpected?: boolean;
}

export function ExpectedCommand({
  expectedCommand,
  validationRegex,
  showExpected = false,
}: ExpectedCommandProps) {
  if (!showExpected) return null;

  return (
    <div className="rounded border border-slate-700/50 bg-slate-900/30 px-2 py-1.5 text-xs">
      <div className="flex items-center gap-1.5 text-slate-500">
        <Terminal className="h-3 w-3" />
        <code className="font-mono text-git-orange">{expectedCommand}</code>
      </div>
      {validationRegex && (
        <div className="mt-1 text-[10px] text-slate-600">
          Regex: <code>{validationRegex}</code>
        </div>
      )}
    </div>
  );
}

/**
 * Компонент для отображения статуса выполнения команды
 */
interface CommandStatusProps {
  status: 'idle' | 'success' | 'error' | 'hint';
  message?: string;
  /** Компактный режим */
  compact?: boolean;
}

export function CommandStatus({ status, message, compact = true }: CommandStatusProps) {
  if (status === 'idle') return null;

  const statusConfig = {
    success: {
      icon: '✓',
      color: 'text-green-400 bg-green-400/10 border-green-400/20',
      label: compact ? '' : 'Правильно!',
    },
    error: {
      icon: '✗',
      color: 'text-red-400 bg-red-400/10 border-red-400/20',
      label: compact ? '' : 'Ошибка',
    },
    hint: {
      icon: '💡',
      color: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
      label: compact ? '' : 'Подсказка',
    },
    idle: {
      icon: '',
      color: '',
      label: '',
    },
  };

  const config = statusConfig[status];

  if (compact) {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs',
          config.color
        )}
      >
        <span>{config.icon}</span>
        {message && <span>{message}</span>}
      </div>
    );
  }

  return (
    <div className={cn('rounded-lg border px-3 py-2 flex items-center gap-2', config.color)}>
      <span className="text-sm">{config.icon}</span>
      <div>
        <span className="text-sm font-medium">{config.label}</span>
        {message && <p className="text-xs opacity-80 mt-0.5">{message}</p>}
      </div>
    </div>
  );
}
